import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import { X } from "@phosphor-icons/react";
import { useTheme } from "@mui/styles";
import { Alert, Stack, Typography } from "@mui/material";
import {
  cancelReasons,
  findCancelReasonByResponsible,
  getKeyCancellationReason,
} from "../../constants/cancelReasons";
import Divider from "@mui/material/Divider";
import { useSelector } from "react-redux";
import * as selectors from "../../selectors";
import { LoadingButton } from "@mui/lab";

const CancelSessionDialog = (props) => {
  const {
    sessionDetails,
    open,
    closeDialog,
    onChangeCancelReason,
    cancelReasonText,
    submitCancelSession,
    responsibleForCancellation,
    onChangeResponsibleForCancellation,
    disableClose,
    cancelAllInstances,
    onChangeCancelSelection,
    hideAlert = false,
    loading, // Add loading prop
    cancellationReasons,
    recurringCancellationReasons,
    cancellationReasonsLoading = false,
    recurringCancellationReasonsLoading = false,
    cancellationReasonId,
  } = props;

  const theme = useTheme();
  const cancelVideoCallLoading = useSelector(selectors.getCancelVideoCallLoading);
  
  // Use the passed loading prop if available, otherwise fall back to Redux state
  const isLoading = loading !== undefined ? loading : cancelVideoCallLoading;
  const isSeriesCancellation = cancelAllInstances === true || cancelAllInstances === "true";
  const availableCancellationReasons = isSeriesCancellation
    ? recurringCancellationReasons
    : cancellationReasons;
  const catalogLoading = isSeriesCancellation
    ? recurringCancellationReasonsLoading
    : cancellationReasonsLoading;
  const usesCatalog =
    Array.isArray(availableCancellationReasons) && availableCancellationReasons.length > 0;
  const catalogResponsibleForCancellation = usesCatalog
    ? getKeyCancellationReason(responsibleForCancellation)
    : responsibleForCancellation;
  const legacyCancellationReason = findCancelReasonByResponsible(responsibleForCancellation);
  const responsibleOptions = usesCatalog
    ? (availableCancellationReasons || []).filter(
        (reason, index, reasons) =>
          reasons.findIndex(
            (candidate) =>
              candidate.responsibleForCancellation === reason.responsibleForCancellation
          ) === index
      )
    : [];
  const selectedCatalogReasons = usesCatalog
    ? isSeriesCancellation
      ? availableCancellationReasons || []
      : (availableCancellationReasons || []).filter(
          (reason) => reason.responsibleForCancellation === catalogResponsibleForCancellation
        )
    : [];
  const selectedCatalogReason = selectedCatalogReasons.find(
    (reason) => String(reason.id) === String(cancellationReasonId)
  );
  const selectedCatalogReasonId = selectedCatalogReason?.id;
  return (
    <Dialog maxWidth="sm" open={open}>
      <DialogTitle>
        Cancel Therapy Session
        {!disableClose && (
          <IconButton aria-label="close" onClick={closeDialog} disabled={isLoading}>
            <X />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent style={{ paddingTop: 0, paddingBottom: "20px" }}>
        <Stack gap={theme.spacing(5)}>
          {sessionDetails?.is_recurring && (
            <FormControl disabled={isLoading}>
              <RadioGroup
                aria-label="responsibleForCancellation"
                name="plan"
                value={cancelAllInstances}
                onChange={onChangeCancelSelection}
              >
                <FormControlLabel
                  value={false}
                  control={<Radio color="primary" size="small" />}
                  label={<Typography>Cancel only this session</Typography>}
                />
                <FormControlLabel
                  value={true}
                  control={<Radio color="primary" size="small" />}
                  label={<Typography>Cancel this and all future sessions</Typography>}
                />
              </RadioGroup>
              <Divider variant="fullWidth" style={{ marginTop: "16px", marginBottom: "16px" }} />
            </FormControl>
          )}
          {(!isSeriesCancellation || !usesCatalog) && (
            <FormControl disabled={isLoading || catalogLoading}>
              <FormLabel sx={{ color: theme.palette.text.primary }}>
                Who needs to cancel the session?
              </FormLabel>
              <RadioGroup
                aria-label="responsibleForCancellation"
                name="responsibleForCancellation"
                value={catalogResponsibleForCancellation}
                onChange={(e) => {
                  onChangeResponsibleForCancellation(e);
                }}
              >
                {usesCatalog
                  ? responsibleOptions.map((reason) => (
                      <FormControlLabel
                        value={reason.responsibleForCancellation}
                        control={<Radio color="primary" size="small" />}
                        label={reason.responsibleLabel}
                        key={reason.responsibleForCancellation}
                      />
                    ))
                  : cancelReasons.map((reason, i) => {
                      const isObject = !Array.isArray(reason.cancelReasons);
                      const labelContent =
                        isObject && reason.cancelReasons.label
                          ? `${reason.label} ${reason.cancelReasons.label}`
                          : reason.label;
                      return (
                        <FormControlLabel
                          value={reason.responsibleForCancellation}
                          control={<Radio color="primary" size="small" />}
                          label={labelContent}
                          key={i}
                        />
                      );
                    })}
              </RadioGroup>
            </FormControl>
          )}
          {(usesCatalog ||
            !(
              responsibleForCancellation &&
              !Array.isArray(legacyCancellationReason?.cancelReasons)
            )) && (
            <TextField
              id="cancelReasonText"
              name="cancelReasonText"
              label="Reason for cancelling"
              autoComplete="off"
              onChange={(e) => onChangeCancelReason(e)}
              value={usesCatalog ? selectedCatalogReasonId ?? "" : cancelReasonText}
              select
              sx={{ width: 256 }}
              SelectProps={{ required: true }}
              disabled={isLoading || catalogLoading}
            >
              <MenuItem disabled value="">
                Select
              </MenuItem>
              {usesCatalog
                ? selectedCatalogReasons.map((reason) => (
                    <MenuItem key={reason.id} value={reason.id}>
                      {reason.displayLabel}
                    </MenuItem>
                  ))
                : cancelReasons
                    .filter(
                      (item) =>
                        item.responsibleForCancellation === responsibleForCancellation &&
                        Array.isArray(item.cancelReasons)
                    )
                    .flatMap((item) => item.cancelReasons)
                    .map((cancelReason, i) => {
                      return (
                        <MenuItem key={i} value={cancelReason}>
                          {cancelReason}
                        </MenuItem>
                      );
                    })}
            </TextField>
          )}
          {!hideAlert && (
            <Alert severity="warning">
              Cancelling a therapy session will send a confirmation email to the client about the
              cancellation
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} color="secondary" disabled={isLoading}>
          {disableClose ? "Go Back" : "Cancel"}
        </Button>
        <LoadingButton
          onClick={submitCancelSession}
          color="error"
          loading={isLoading}
          disabled={
            catalogLoading ||
            !cancelReasonText ||
            !responsibleForCancellation ||
            (usesCatalog && !selectedCatalogReason)
          }
        >
          Cancel therapy session
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CancelSessionDialog;
